import { motion } from "framer-motion";
import { scrollToSection } from "../../lib/scroll";

const docs = [
  { img: "/resources/toolkit.png", title: "IDS Toolkit", tag: "Participant workbook", desc: "The workshop workbook participants take away — reflection, worksheets and applied exercises.", pages: "7 pages" },
  { img: "/resources/runsheet.png", title: "Kowa Run Sheet", tag: "Session design", desc: "The two-hour program, minute by minute — arc, activities and logistics.", pages: "3 pages" },
  { img: "/resources/gstack.png", title: "Governance Stack", tag: "Reference architecture", desc: "First Nations governance architecture for institutional data systems, in five layers.", pages: "2 pages", landscape: true },
  { img: "/resources/product.png", title: "Commercial Product Architecture", tag: "Product suite", desc: "The product ladder — from first conversation to practitioner-level engagement.", pages: "8 pages" },
  { img: "/resources/ratecard.png", title: "Rate Card", tag: "Services & rates", desc: "Services and foundation rates held for organisations booking the workshop.", pages: "1 page" },
];

export default function WorkshopResources() {
  return (
    <section id="resources" data-testid="resources-section" className="py-24 md:py-32 bg-surface1 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ochre mb-6">The workshop package</p>
        <h2 data-testid="resources-heading" className="font-serif text-3xl md:text-5xl tracking-tight mb-4 max-w-3xl">
          Every document, <em className="text-ember">already written.</em>
        </h2>
        <p className="text-stone2 max-w-2xl mb-16 leading-relaxed">
          The workshop is backed by a complete professional resource set —
          workbooks, run sheets, reference architectures and commercial
          documents. Participants receive the package as part of the program.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line/40 border border-line/40">
          {docs.map((d, i) => (
            <motion.article
              key={d.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.07 }}
              data-testid={`resource-card-${i}`}
              className="bg-ink p-5 group hover:bg-surface2 transition-colors duration-300 flex flex-col"
            >
              <div className="relative overflow-hidden border border-line mb-5 aspect-[210/260] bg-surface2">
                <img
                  src={d.img}
                  alt={`${d.title} — first page`}
                  loading="lazy"
                  className={`w-full h-full transition-transform duration-500 group-hover:scale-[1.03] ${d.landscape ? "object-cover object-left-top" : "object-cover object-top"}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent pointer-events-none" />
                <span className="absolute bottom-2 right-2 font-mono text-[9px] uppercase tracking-[0.15em] bg-ink/85 text-stone2 px-2 py-1">{d.pages}</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">{d.tag}</span>
              <h3 className="font-serif text-lg leading-snug mb-2 group-hover:text-ember transition-colors duration-300">{d.title}</h3>
              <p className="text-xs text-stone2 leading-relaxed">{d.desc}</p>
            </motion.article>
          ))}

          <div data-testid="resource-cta-card" className="bg-surface2 p-5 flex flex-col justify-between border-t-2 border-ember">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ochre mb-2 block">Included with every workshop</span>
              <h3 className="font-serif text-xl leading-snug mb-3">The package travels with the program.</h3>
              <p className="text-xs text-stone2 leading-relaxed">
                Resources are provided to participants during delivery and are previews only — not available for public download.
              </p>
            </div>
            <div className="flex flex-col gap-3 mt-6">
              <button type="button" onClick={() => scrollToSection("book")} data-testid="resources-book-btn" className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 text-center">
                Book the workshop
              </button>
              <button type="button" onClick={() => scrollToSection("briefing")} data-testid="resources-briefing-btn" className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-3.5 border border-line text-bone hover:border-ember hover:text-ember transition-colors duration-300 text-center">
                Request a confidential briefing
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
